import { useQueryClient } from '@tanstack/react-query';
import { Form, message } from 'antd';
import { Store } from 'antd/es/form/interface';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

import {
  ProductDetailsCard,
  CustomerDetailsCard,
  PackageAndPaymentCard,
  DeliveryInstructionCard,
  OrderIdCard,
  ServiceLevelCard,
  PickupAddressCard,
} from '@/components/OrderForm';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import { ROUTE } from '@/config';
import { useChangeProduct, useCustomerIO, useUpdateOrderPricePreview } from '@/hooks';
import usePermission from '@/hooks/usePermission';
import {
  useAsyncCreateOrders,
  useCreateContact,
  useFetchServicePreferences,
  useFetchUser,
  useGetAvailableCarriers,
} from '@/hooksApi';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import useHasServiceChoice from '@/hooks/useHasServiceChoice';
import useServiceStepDecision from '@/hooks/useServiceStepDecision';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';
import useProductStore from '@/stores/productStore';
import useNewOrderStore from '@/stores/useNewOrderStore';
import useUserStore, { selectUsesServices } from '@/stores/userStore';
import {
  AvailableCarrierItem,
  AvailableCarriersAdaptedResponse,
  BasicProduct,
  ContactRequest,
  Order,
  OrderBoxInput,
  PaymentType,
  ServiceTypeEnum,
  ShipmentFeeType,
  PermissionName,
} from '@/types';
import { useConfirmModalStore } from '@/stores';
import useOrderTypeStore, { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import {
  adjustProductListByOrderType,
  checkProductValidation,
  getOrderTypeTextKey,
  buildAvailableCarriersRequest,
  processCarrierPreviewResponse,
} from '@/utils';
import { handleErrorMessage, stripBulkRowPrefix } from '@/utils/handleError';
import { ContactData } from '@/components/OrderForm/CustomerDetailsCard';
import NoCarrierModal from '@/components/OrderForm/NoCarrierModal';
import { buildRestoreFormValues, buildRestoreProducts } from './restoreOrderForm';
import { SmartPasteCard } from '@/components/SmartPaste';
import { useSmartPasteFill } from '@/hooks/useSmartPasteFill';
import type { SmartPasteOrder } from '@/types/smartPaste';
import {
  carrierRefusalReason,
  carrierRefusalCode,
  failedRowRefusal,
  refusalServiceCode,
} from '@/utils/carrierRefusalReason';

const AddNewOrder = () => {
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string | undefined>(undefined);
  const [addOrderLoading, setAddOrderLoading] = useState<'creating' | 'ready_to_ship' | undefined>(
    undefined,
  );
  const [showNoCarrierModal, setShowNoCarrierModal] = useState(false);
  // R56-F3 — the backend's localized refusal for the CURRENT failure.
  // Cleared on every open so a stale reason can never explain a new one.
  const [noCarrierReason, setNoCarrierReason] = useState<string | undefined>();
  // BOXY-301 (QA F-DASH-2) — travels with the sentence so the Boxy Select
  // branch of NoCarrierModal can tell a refusal it may safely render from one
  // it may not.
  const [noCarrierReasonCode, setNoCarrierReasonCode] = useState<string | undefined>();
  // BOXY-501 — the service a MULTI_BOX_NEEDS_OTHER_SERVICE refusal named.
  const [noCarrierServiceCode, setNoCarrierServiceCode] = useState<string | undefined>();
  const { data: servicePreferences } = useFetchServicePreferences();

  const scrollRef = useRef<HTMLDivElement>(null);
  // Gates the auto-derive-fragile effect. Kept false during back-nav restore so
  // the effect cannot clobber the restored order-level `isFragile` before the
  // restored products (with their `is_fragile`) have hydrated. Enabled after a
  // fresh entry immediately, and after a restore once hydration settles.
  const fragileSyncEnabledRef = useRef(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const usesServices = useUserStore(selectUsesServices);
  const isRTL = i18n.dir() === 'rtl';
  const queryClient = useQueryClient();

  const {
    orderPayload: existingOrderPayload,
    savedProvinceCode,
    setOrderPayload: setStoreOrderPayload,
    setCarrierSelection,
    setSelectedCarrier,
    setSourceRegionUid,
    setContactDisplayInfo,
    setSavedProvinceCode,
    clearEditMode,
    contactDisplayInfo,
    reset: resetOrderStore,
  } = useNewOrderStore();
  const { mutate: mutateAsyncAddNewOrder } = useAsyncCreateOrders();
  const { mutate: mutateAddNewContact } = useCreateContact();

  // Separate mutation instances: one for inline preview, one for navigation fetch
  // This prevents the two from racing and overwriting each other's callbacks
  const { mutate: fetchCarrierPreviewInline } = useGetAvailableCarriers();
  const { mutate: fetchCarrierPreviewNavigate, isLoading: carrierPreviewLoading } =
    useGetAvailableCarriers();

  // SVC-STEP — shared with the stepper and the edit flow, so all three agree.
  const hasServiceChoice = useHasServiceChoice();

  // SVC-STEP increment 2 — the step is skipped when something the merchant is
  // BOUND TO already decided (their plan, a province rule, their saved
  // default), and shown when nothing has. Keyed on the destination, because
  // that is the only way a province rule can be honoured; and never skipped on
  // an unknown answer, because a wrong skip ships a service they never saw
  // while a wrong show costs one click.
  // Watched, not read once: the merchant can tick Temperature Control at any
  // point, and the skip decision has to change with it — that is the whole
  // reported flow (a Baghdad→Boxy Save rule plus a cold Baghdad order).
  const temperatureControl = Boolean(Form.useWatch('temperatureControl', form));

  // The resolution itself is no longer read on this page (BOXY-237 removed the
  // service block); only the SKIP decision is, and it still needs the cold flag
  // — a rule that cannot carry this order must route the merchant to the step.
  // BOXY-113 §6.3 — the destination region too: a remote address whose
  // preselected service cannot reach it must SHOW the step (Select preselected).
  const destinationRegionUid = Form.useWatch('region_uid', form) as string | undefined;
  // BOXY-530 — and the pickup: a Basra pickup can only ship Boxy Select.
  const savedPickUpAddressUid = Form.useWatch('saved_pick_up_address_uid', form) as
    | string
    | undefined;
  const pickUpAddressUid = Form.useWatch('pick_up_address_uid', form) as string | undefined;
  // BOXY-547 — and the boxes: a multi-box order can only ship where a carrier takes it.
  const watchedBoxes = Form.useWatch('boxes', form) as OrderBoxInput[] | undefined;
  const { shouldShowStep } = useServiceStepDecision(
    selectedProvinceCode,
    true,
    temperatureControl,
    destinationRegionUid,
    { saved_pick_up_address_uid: savedPickUpAddressUid, pick_up_address_uid: pickUpAddressUid },
    watchedBoxes,
  );
  // BOXY-612 — the skip rests on what the merchant has SAVED. A preference
  // changed elsewhere (the app, another tab) inside the 5-minute stale window
  // must count on this order, so the form re-reads it on entry.
  useEffect(() => {
    queryClient.invalidateQueries(['service-preferences']);
  }, [queryClient]);

  const { data: pickupAddressesData } = useFetchPickupAddresses();
  const { data: provinceList } = useFetchListProvinces();
  const { data: areaList } = useFetchAreaByProvinceCode(selectedProvinceCode || '');

  const { data: userData } = useFetchUser();
  const { openConfirm } = useConfirmModalStore();
  const { trackEvent } = useCustomerIO();
  const { hasPermission } = usePermission();

  const { products, resetProducts, setSubmitAttempted } = useProductStore();
  const { orderType, resetOrderType, setOrderType } = useOrderTypeStore();

  // Callback to trigger carrier preview when price preview is requested
  const handleCarrierPreviewTrigger = useCallback(
    (orderData: Order) => {
      // Find the selected pickup address to get source region
      const selectedPickupAddress = pickupAddressesData?.object?.items?.find(
        (addr) =>
          addr.uid === orderData.saved_pick_up_address_uid ||
          addr.uid === orderData.pick_up_address_uid,
      );

      const source_region_uid = selectedPickupAddress?.region?.uid;

      if (!source_region_uid) {
        console.warn('Pickup address not found for carrier preview');
        return;
      }

      if (!orderData.contact?.region_uid) {
        console.warn('Customer region UID missing for carrier preview');
        return;
      }

      // Build carrier preview request using shared utility
      const carrierRequest = buildAvailableCarriersRequest(orderData, source_region_uid);

      // Only a refusal matters here — no merchant is shown a carrier.
      fetchCarrierPreviewInline(carrierRequest, {
        onError: (error: any) => {
          console.error('Failed to fetch carrier preview:', error);
          if (error?.response?.data?.code === 760001) {
            setNoCarrierReason(carrierRefusalReason(error));
            setNoCarrierReasonCode(carrierRefusalCode(error));
            setNoCarrierServiceCode(refusalServiceCode(error));
            setShowNoCarrierModal(true);
          }
        },
      });
    },
    [fetchCarrierPreviewInline, pickupAddressesData],
  );

  const {
    handleFormUpdated,
    orderPrice,
    orderPriceLoading,
    customShipmentFee,
    setCustomShipmentFee,
    setPackageSize,
    transformValuesToOrder,
    checkOrderFormValidation,
    handleProductChanged,
    isPreviewCalled,
    previewBlockedReason,
  } = useUpdateOrderPricePreview(products, form, false, handleCarrierPreviewTrigger);

  const {
    handleAddProduct,
    handleAddProducts,
    handleDeleteProduct,
    handleUpdateProduct,
    handleUpdateProductBatch,
    setProducts,
    setIsProductListValid,
  } = useChangeProduct(handleProductChanged, orderType);

  useEffect(() => {
    // Only reset form on fresh entry — skip when returning from carrier selection
    if (!existingOrderPayload) {
      // Fresh entry: clear edit mode flag and reset all form/store state
      clearEditMode();
      form.resetFields();
      resetProducts();
      resetOrderType();
      resetOrderStore();
      // Fresh entry: auto-derive-fragile may run normally from the start.
      fragileSyncEnabledRef.current = true;
    } else {
      // Returning from carrier selection — restore form fields from the stored payload
      // Clear stale carrier selection so it gets rebuilt fresh on next "Next" click
      setCarrierSelection(null);

      const contact = existingOrderPayload.contact;
      if (contact) {
        if (savedProvinceCode) {
          setSelectedProvinceCode(savedProvinceCode);
        }
        form.setFieldsValue(buildRestoreFormValues(existingOrderPayload, savedProvinceCode));
        // Keep the Zustand order-type store in sync so OrderTypeCard re-highlights
        // the correct selection (form field alone does not update the store).
        setOrderType((existingOrderPayload.type as OrderTypeEnum) ?? OrderTypeEnum.NORMAL);
      }
      if (existingOrderPayload.products?.length) {
        // Preserve is_fragile / uid / product_catalog_uid so fragility and
        // catalog linkage survive back-nav (see buildRestoreProducts).
        setProducts(buildRestoreProducts(existingOrderPayload.products));
      }

      // Ant Design form.setFieldsValue() does NOT trigger onValuesChange,
      // so manually trigger the price preview after restoring form data
      setTimeout(() => {
        const allValues = form.getFieldsValue();
        handleFormUpdated({ region_uid: allValues.region_uid }, allValues);
        // Re-enable fragile auto-derive only after the restored products and
        // form values have hydrated, so it can't wipe the restored isFragile.
        fragileSyncEnabledRef.current = true;
      }, 0);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
      setIsScrolledUp(!atBottom);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the source region UID for the currently selected pickup address
  const resolveSourceRegionUid = useCallback(
    (formValues: Store): string | undefined => {
      const selectedPickupAddress = pickupAddressesData?.object?.items?.find(
        (addr) =>
          addr.uid === formValues.saved_pick_up_address_uid ||
          addr.uid === formValues.pick_up_address_uid,
      );
      return selectedPickupAddress?.region?.uid;
    },
    [pickupAddressesData],
  );

  const handleSubmit = (ready_to_ship: boolean) => {
    const formValues = form.getFieldsValue();

    setSubmitAttempted(true);

    // Check for partially filled products (has some data but missing required fields)
    const partiallyFilled = products.filter((p) => {
      const hasTitle = !!p.title;
      const hasPrice = !!p.price || p.price === 0;
      return (hasTitle || hasPrice) && !(hasTitle && hasPrice);
    });

    if (partiallyFilled.length > 0) {
      message.error(t('please_fill_all_product_fields'));
      return;
    }

    // Filter out completely empty products
    const finalProducts = products.filter((p) => checkProductValidation(p));

    const isValid = checkOrderFormValidation({ ...formValues, products: finalProducts });
    setIsProductListValid(finalProducts.length > 0);

    form.submit();
    form
      .validateFields()
      .then((values) => {
        if (isValid) onFinish(values, ready_to_ship, finalProducts);
      })
      .catch((errorInfo) => {
        console.warn('Failed: ', errorInfo);
      });
  };

  const onFinish = (values: Store, ready_to_ship: boolean, finalProducts: BasicProduct[]) => {
    if (userData?.object.verified_at) {
      const orderPayload = transformValuesToOrder(
        { ...values, products: finalProducts },
        ready_to_ship,
        true,
      );

      if (values.saveToAddress) {
        saveContact(orderPayload);
      }

      checkCarriersAndNavigate(orderPayload, ready_to_ship, values);
    } else {
      openNotVerifiedConfirm();
    }
  };

  // Check this order can ship, then go to the service step (or the summary).
  // No merchant picks a carrier (owner 2026-10-28), so there is no rule
  // evaluation and no carrier step — the flow Select mode always had.
  const checkCarriersAndNavigate = (
    orderPayload: Order,
    ready_to_ship: boolean,
    formValues: Store,
  ) => {
    const source_region_uid = resolveSourceRegionUid(formValues);

    if (!source_region_uid || !orderPayload.contact?.region_uid) {
      console.warn('Cannot check carrier availability: missing pickup or destination region.');
      setStoreOrderPayload(orderPayload);
      // Client-side precondition — no backend answer exists, so the generic copy.
      setNoCarrierReason(undefined);
      setNoCarrierReasonCode(undefined);
      setNoCarrierServiceCode(undefined);
      setShowNoCarrierModal(true);
      return;
    }

    setAddOrderLoading('creating');

    proceedWithCarrierFetch(orderPayload, source_region_uid);
  };

  const proceedWithCarrierFetch = (orderPayload: Order, source_region_uid: string) => {
    const carrierRequest = buildAvailableCarriersRequest(orderPayload, source_region_uid);

    fetchCarrierPreviewNavigate(carrierRequest, {
      onSuccess: (response: AvailableCarriersAdaptedResponse) => {
        const allCarriers = processCarrierPreviewResponse(response);

        const hasCarriers = allCarriers.length > 0;
        if (!hasCarriers) {
          setAddOrderLoading(undefined);
          setStoreOrderPayload(orderPayload);
          // A 200 with nothing in it — there is no refusal sentence to
          // quote, so keep the generic copy.
          setNoCarrierReason(undefined);
          setNoCarrierReasonCode(undefined);
          setNoCarrierServiceCode(undefined);
          setShowNoCarrierModal(true);
          return;
        }

        // Build AvailableCarrierItem[] from the adapted response for the store
        const available: AvailableCarrierItem[] = allCarriers.map((c) => ({
          carrier_uid: (c.carrier as any).uid,
          carrier_slug: c.carrier.slug || '',
          carrier_name: c.carrier.title,
          carrier_logo_url: c.carrier.avatar?.abs_url || '',
          shipping_fee: parseFloat(c.shipping_fee) || 0,
          delivery_day:
            c.serviceType === ServiceTypeEnum.SAME_DAY ? ('same' as const) : ('next' as const),
          same_day_cutoff: c.serviceType === ServiceTypeEnum.SAME_DAY ? c.if_ship_before : null,
          drop_off_time: c.serviceType === ServiceTypeEnum.NEXT_DAY ? c.if_ship_before : null,
        }));

        setStoreOrderPayload(orderPayload);
        setSourceRegionUid(source_region_uid);
        setSavedProvinceCode(selectedProvinceCode || null);

        // Resolve province/area display names for the summary page
        const provinceName =
          provinceList?.object?.find((p) => p.province_code === selectedProvinceCode)?.province ??
          '';
        const areaName =
          areaList?.object?.find((a) => a.uid === orderPayload.contact?.region_uid)?.area_name ??
          '';
        // provinceCode rides with the name it was derived from, so the
        // summary's trace can never resolve destination-blind (QA INC2-F2).
        setContactDisplayInfo({
          provinceName,
          areaName,
          provinceCode: selectedProvinceCode ?? null,
        });
        setCarrierSelection({
          process_id: response.process_id,
          recommended_carrier_slug: response.recommended_carrier_slug,
          available_carriers: available,
        });
        setAddOrderLoading(undefined);
        // There is no carrier step (owner 2026-10-28). For a Select merchant
        // the backend returns a single "Boxy" service entry (§6 item 4); the
        // pick is never sent, so taking the first entry is only the price
        // the summary falls back to while its preview loads.
        setSelectedCarrier(available[0] ?? null);
        // SVC-STEP: the service step only when there is a choice to make.
        navigate(hasServiceChoice && shouldShowStep ? ROUTE.SELECT_SERVICE : ROUTE.ORDER_SUMMARY);
      },
      onError: (error: any) => {
        console.error('Failed to fetch carrier availability on submit:', error);
        setAddOrderLoading(undefined);
        setStoreOrderPayload(orderPayload);

        // A 422 IS NOT A COVERAGE PROBLEM. Every failure here used to open the
        // no-carrier modal — "we couldn't find a delivery option for this
        // order" — including a plain validation refusal. Those have different
        // causes and different fixes, and the merchant was sent hunting for a
        // carrier outage over a field they typed.
        //
        // The live trigger: a local-format number with a leading zero entered
        // while the country selector already holds +964 produces
        // `+9640770…`, which fails the phone rule. UI-QA burned four browser
        // runs chasing a phantom outage before reproducing it against the API.
        //
        // The answer is already in the response — `object` is Laravel's
        // field-error map — and was being discarded. handleErrorMessage is the
        // app's existing 422 surface (it already filters unrenderable entries
        // and holds the toast for 8s), so this reuses it rather than growing a
        // second one.
        // BOXY-501 — a multi-box order on a service that cannot carry boxes:
        // a refusal with a remedy, not a field error — the modal, with a switch.
        if (carrierRefusalCode(error) === 'MULTI_BOX_NEEDS_OTHER_SERVICE') {
          setNoCarrierReason(failedRowRefusal(error) ?? carrierRefusalReason(error));
          setNoCarrierReasonCode('MULTI_BOX_NEEDS_OTHER_SERVICE');
          setNoCarrierServiceCode(refusalServiceCode(error));
          setShowNoCarrierModal(true);
          return;
        }
        if (error?.response?.status === 422) {
          const data = error?.response?.data;
          handleErrorMessage({
            ...data,
            statusCode: 422,
            // Wire paths are array-indexed because the endpoint is a BULK one,
            // but the merchant filled in a single form and "orders.0." names a
            // row they never saw.
            object: stripBulkRowPrefix(data?.object),
          });
          return;
        }

        // R56-F3 — this is the modal a merchant with an unservable parcel
        // actually meets. Show what the backend said, not a guess about
        // carriers they never switched off.
        setNoCarrierReason(carrierRefusalReason(error));
        setNoCarrierReasonCode(carrierRefusalCode(error));
        setNoCarrierServiceCode(refusalServiceCode(error));
        setShowNoCarrierModal(true);
      },
    });
  };

  const saveContact = (orderPayload: Order) => {
    const contactPayload = { ...orderPayload.contact } as ContactRequest;

    mutateAddNewContact(contactPayload, {
      onSuccess: () => {
        queryClient.refetchQueries(['contacts']);
        message?.success(t('contact_added'));
      },
      onError: () => {
        message?.error(t('operation_failed_please_try_again'));
      },
    });
  };

  const openNotVerifiedConfirm = () => {
    openConfirm({
      description: t('user_not_verified'),
      onConfirm: () => {
        trackEvent(
          'request_verification',
          {
            userId: userData?.object?.uid,
            traits: {
              phone: userData?.object?.phone,
              user_type: 'MERCHANT',
              email: userData?.object?.email,
              firstname: userData?.object?.first_name,
              lastname: userData?.object?.last_name,
              fullname: `${userData?.object?.first_name}${userData?.object?.last_name}`,
            },
          },
          t('thank_you_for_choosing_Boxy_we_will_contact_you_soon'),
        );
      },
      approveText: t('request_activation'),
      cancelText: t('cancel'),
    });
  };

  const handleCreateProduct = () => {
    handleAddProduct({ title: '', quantity: '1', price: '' });
  };

  // Auto-derive order-level fragile from products — set true when any is fragile, clear when none are.
  // Suppressed during back-nav restore so it can't clobber the restored isFragile mid-hydration.
  useEffect(() => {
    if (!fragileSyncEnabledRef.current) return;
    const anyFragile = products.some((p) => p.is_fragile === true);
    const currentFragile = form.getFieldValue('isFragile');
    if (anyFragile && !currentFragile) {
      form.setFieldsValue({ isFragile: true });
    } else if (!anyFragile && currentFragile) {
      form.setFieldsValue({ isFragile: false });
    }
  }, [products]);

  // Smart Paste — drop a parsed order into the form (pre-fill only).
  const applySmartPaste = useSmartPasteFill({
    form,
    products,
    provinces: provinceList?.object,
    setSelectedProvinceCode,
    setProducts,
    setOrderType,
    onFilled: (allValues, filledProducts) =>
      handleFormUpdated(
        { region_uid: allValues.region_uid },
        { ...allValues, products: filledProducts },
      ),
  });

  // Same guard the back-nav restore uses: the fill sets the product list AND
  // the order-level fragile flag, and the auto-derive effect above would run in
  // between and clear the flag again (Smart Paste marks fragility on the order,
  // never per product). Re-enabled on the next macrotask, after the effect.
  const handleSmartPasteApply = (order: SmartPasteOrder) => {
    fragileSyncEnabledRef.current = false;
    // Hand the gap list straight back to the card — swallowing it here leaves
    // the merchant with a silently half-filled form and no explanation.
    const gaps = applySmartPaste(order);
    setTimeout(() => {
      fragileSyncEnabledRef.current = true;
    }, 0);
    return gaps;
  };

  const handleSetSelectedProvinceCode = (provinceCode?: string) => {
    form.setFieldsValue({ province: provinceCode, region_uid: undefined });
    form.setFieldValue('region_uid', undefined);

    setSelectedProvinceCode(provinceCode);
  };

  const handleFormUpdateForAddress = (formValue: Store) => {
    handleFormUpdated(
      { saved_pick_up_address_uid: formValue.saved_pick_up_address_uid },
      { ...formValue },
    );
  };

  const handleOrderTypeChange = (newOrderType: OrderTypeEnum) => {
    const allValues = form.getFieldsValue();
    const updatedProducts = adjustProductListByOrderType(products, newOrderType);
    setProducts(updatedProducts);
    handleFormUpdated(
      { orderType: newOrderType },
      { ...allValues, orderType: newOrderType, products: updatedProducts },
    );
  };

  // BOXY-501 — "Use Boxy Select": put the named service on the form; the
  // price preview and the picker re-ask with it (service_code is forwarded).
  const switchServiceTitle = servicePreferences?.services?.find(
    (service) => service.code === noCarrierServiceCode,
  )?.title;
  const switchService =
    usesServices &&
    noCarrierReasonCode === 'MULTI_BOX_NEEDS_OTHER_SERVICE' &&
    noCarrierServiceCode &&
    switchServiceTitle
      ? {
          title: switchServiceTitle,
          onSwitch: () => {
            form.setFieldValue('serviceCode', noCarrierServiceCode);
            onFormValuesChange({ serviceCode: noCarrierServiceCode }, form.getFieldsValue());
          },
        }
      : undefined;

  const onFormValuesChange = (changedValues: any, allValues: Store) => {
    const changedItem = Object.keys(changedValues)[0];

    if (changedItem === 'orderType') {
      const updatedProducts = adjustProductListByOrderType(products, allValues.orderType);
      setProducts(updatedProducts);
      handleFormUpdated(changedValues, { ...allValues, products: updatedProducts });
    } else {
      handleFormUpdated(changedValues, allValues);
    }
  };

  const handleUpdatePreviewByContactSelection = (contact: ContactData) => {
    const allValues = form.getFieldsValue();

    handleFormUpdated({ address: contact.address || '' }, { ...allValues, ...contact });
  };

  return (
    <div className='sd-scope sd-font relative flex flex-col -mx-4 sm:-mx-8 -mt-6 -mb-6 h-[calc(100dvh-65px)] overflow-hidden'>
      <NoCarrierModal
        open={showNoCarrierModal}
        onClose={() => setShowNoCarrierModal(false)}
        reason={noCarrierReason}
        reasonCode={noCarrierReasonCode}
        coldRequested={temperatureControl}
        switchService={switchService}
      />
      <div ref={scrollRef} className='flex-1 overflow-y-auto px-4 sm:px-8 pt-4 sm:pt-6 pb-40'>
        <Form
          form={form}
          initialValues={{
            packageSize: 'S',
            CustomerOrMerchant: ShipmentFeeType.BY_MERCHANT,
            CodOrPrepaid: PaymentType.COLLECT_ON_DELIVERY,
          }}
          name='add-new-order'
          onValuesChange={onFormValuesChange}
        >
          {/* Breadcrumb navigation */}
          <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground mb-6'>
            <Link
              to='/orders'
              className='text-sd-muted-foreground hover:text-sd-foreground transition-colors no-underline'
            >
              {String(t('orders'))}
            </Link>
            <ChevronRight className={`h-3.5 w-3.5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className='text-sd-foreground font-medium'>{String(t('new_order'))}</span>
          </nav>

          <OrderStepper currentStep={1} provinceCode={selectedProvinceCode} />

          {/* Sits under the stepper: above it, the full-width card broke the
              breadcrumb/stepper rhythm. */}
          <SmartPasteCard onApply={handleSmartPasteApply} />

          <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6'>
            {/* Left column */}
            <div className='flex flex-col gap-6'>
              <CustomerDetailsCard
                form={form}
                handleUpdatePreview={handleUpdatePreviewByContactSelection}
                setSelectedProvinceCode={handleSetSelectedProvinceCode}
                selectedProvinceCode={selectedProvinceCode}
              />
              <DeliveryInstructionCard />
              <OrderIdCard />
              <ProductDetailsCard
                products={products}
                handleCreateProduct={handleCreateProduct}
                handleDeleteProduct={handleDeleteProduct}
                handleUpdateProduct={handleUpdateProduct}
                handleUpdateProductBatch={handleUpdateProductBatch}
                handleAddProducts={handleAddProducts}
              />
              <PickupAddressCard form={form} previewOrderPrice={handleFormUpdateForAddress} />
            </div>
            {/* Right column */}
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col gap-6'>
                {/* Above the fee, because it changes it — the form's
                    onValuesChange re-runs the price preview on selection. */}
                <ServiceLevelCard />
                {/* BOXY-237 (Alhassan, 2026-08-30) — NO SERVICE BLOCK ON THIS
                    PAGE AT ALL. Not the [Change] link, not the trace either:
                    "we dont need to display this option, he already will see
                    it in order summary page."

                    The merchant is still told what decided their service — on
                    Order Summary, where ServiceTraceLine renders the same
                    sentence WITH the link. Saying it here as well was the same
                    decision announced twice, on the screen where it cannot yet
                    be acted on, and the order does not exist. */}
                <PackageAndPaymentCard
                  orderOptions={[]}
                  form={form}
                  onPackageSizeChange={setPackageSize}
                  orderPrice={{
                    ...orderPrice,
                    orders_shipment_fee: 0,
                    orders_custom_shipment_fee: 0,
                  }}
                  orderPriceLoading={orderPriceLoading}
                  onCustomShipmentFeeChange={setCustomShipmentFee}
                  customShipmentFee={customShipmentFee}
                  onOrderTypeChange={handleOrderTypeChange}
                  hasFragileProduct={products.some((p) => p.is_fragile === true)}
                  blockedReason={previewBlockedReason}
                />
              </div>
            </div>
          </div>
        </Form>
      </div>

      {/* Floating Footer */}
      <div className='absolute bottom-4 start-0 end-0 z-30 px-4 sm:px-8'>
        <div
          className={cn(
            'rounded-2xl border border-sd-border bg-sd-card px-5 py-4 flex items-center justify-end gap-3 transition-shadow',
            isScrolledUp && 'shadow-lg',
          )}
        >
          {orderType !== OrderTypeEnum.NORMAL && (
            <span className='text-sm font-bold uppercase text-sd-foreground'>
              {String(t(getOrderTypeTextKey(orderType)))}
            </span>
          )}
          <button
            onClick={() => handleSubmit(true)}
            // A blocking refusal means the backend has already said it cannot
            // price this order. Creation would fail with the same message, so
            // the merchant is only allowed forward once they have changed
            // whatever the notice above the summary names.
            disabled={!!addOrderLoading || !!previewBlockedReason}
            title={previewBlockedReason ?? undefined}
            data-testid='submit-order'
            className='inline-flex items-center justify-center gap-2 h-10 px-4 py-2 rounded-md bg-sd-primary text-sm font-medium uppercase text-sd-primary-foreground hover:bg-sd-primary/90 disabled:opacity-50 transition-colors'
          >
            {addOrderLoading === 'creating' ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <>
                {String(t('next', 'NEXT'))}
                <ArrowRight className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} strokeWidth={2} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewOrder;
